// * import third party
import { chunk, isEmpty } from 'lodash';
import { useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { Text, Table, Thead, Tbody, Tr, Th, Td, Tfoot, Flex, Collapse } from '@chakra-ui/react';

// * import local
import { useApp } from '@pages/UploadMappingV2/context/AppContext';
import Pagination from '../ui/Pagination';
import SkeletonComponent from '@components/skeleton';
import EmptyState from '../ui/EmptyState';

export default function TableListProductMapping({
    tableHeadLists = [],
    tableData = [],
    data = [],
    isLoading = false,
    isDefaultExpanded = false,
    isPagination = false,
    isShowEmptyState = true,
    onChangeSort = () => {},
    customExpandedTable = null,
}) {
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(0);
    const splittedData = useMemo(() => chunk(tableData, limit), [tableData, limit]);

    if (!tableHeadLists || !tableData) return <Text>There was an error displaying your data!</Text>;
    const onChangeLimit = (value) => {
        const newLimit = Number(value);
        const calculatedLength = chunk(tableData, newLimit).length;
        const maxPage = Math.max(0, calculatedLength - 1);
        setLimit(newLimit);
        if (page > maxPage) {
            setPage(maxPage);
        }
    };

    const onPageChange = (selected) => {
        setPage(selected.selected);
    };

    const { expanded } = useApp();
    const [sort, setSort] = useState({ key: null, direction: null });

    const toggleSort = (key) => {
        setSort((prev) => {
            let updated;

            if (prev.key !== key) {
                updated = { key, direction: 'asc' };
            } else if (prev.direction === 'asc') {
                updated = { key, direction: 'desc' };
            } else {
                updated = { key: null, direction: null };
            }

            onChangeSort?.(updated);
            return updated;
        });
    };

    const tableHeadMapping = () => {
        return tableHeadLists.map((th) => {
            const isActive = sort.key === th.key;
            const isAsc = isActive && sort.direction === 'asc';
            const isDesc = isActive && sort.direction === 'desc';

            const isCanSort = th.isCanSort ?? false;
            const sortableProps = isCanSort
                ? {
                      cursor: 'pointer',
                      onClick: () => toggleSort(th.key),
                  }
                : {};
            return (
                <Th
                    textTransform={'none'}
                    fontWeight={'semibold'}
                    color={'semantic.600'}
                    fontFamily={'inherit'}
                    fontSize={'inherit'}
                    p={'12px'}
                    letterSpacing={'normal'}
                    width={th.width ?? '200px'}
                    textAlign={th.textAlign ?? 'left'}
                    {...sortableProps}
                >
                    <Flex gap={'8px'} alignItems={'center'}>
                        {th.title}

                        {isCanSort && (
                            <Flex flexDir="column">
                                <ChevronUpIcon opacity={isActive ? (isAsc ? 1 : 0.3) : 1} />
                                <ChevronDownIcon mt={'-9px'} opacity={isActive ? (isDesc ? 1 : 0.3) : 1} />
                            </Flex>
                        )}
                    </Flex>
                </Th>
            );
        });
    };

    const tableBodySkeleton = () => {
        return Array.from({ length: 5 }).map((_, rowIndex) => (
            <Tr key={rowIndex}>
                {tableHeadLists.map((list) => (
                    <Td key={list.key} p={'12px'} textAlign={list.textAlign ?? 'left'}>
                        <SkeletonComponent height={'40px'} />
                    </Td>
                ))}
            </Tr>
        ));
    };

    const tableBodyMapping = () => {
        return splittedData[page]?.map((data, idx) => (
            <>
                <Tr>
                    {tableHeadLists.map((list) => (
                        <Td p={'12px'} fontSize={'14px'} textAlign={list.align ?? 'left'}>
                            {list.tableDataList({
                                value: data[list.key],
                                allValue: { ...data, idx },
                            })}
                        </Td>
                    ))}
                </Tr>

                {data.items && (
                    <Tr>
                        <Td
                            colSpan={5}
                            pb={0}
                            bg="semantic.200"
                            display={isDefaultExpanded || expanded === data.id ? 'table-cell' : 'none'}
                        >
                            {(isDefaultExpanded || expanded === data.id) && customExpandedTable ? (
                                customExpandedTable(data)
                            ) : (
                                <Collapse in={isDefaultExpanded || expanded === data.id} animateOpacity unmountOnExit>
                                    <Table>
                                        <Thead fontWeight={'normal'}>
                                            <Tr>
                                                <Th
                                                    fontWeight={'normal'}
                                                    textTransform={'capitalize'}
                                                    color={'semantic.600'}
                                                    fontFamily={'inherit'}
                                                >
                                                    Brand
                                                </Th>
                                                <Th
                                                    fontWeight={'normal'}
                                                    textTransform={'capitalize'}
                                                    color={'semantic.600'}
                                                    fontFamily={'inherit'}
                                                >
                                                    Produk Paragon
                                                </Th>
                                                <Th
                                                    fontWeight={'normal'}
                                                    textTransform={'capitalize'}
                                                    color={'semantic.600'}
                                                    fontFamily={'inherit'}
                                                >
                                                    Kode Produk Paragon
                                                </Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {data.items.map((item, index) => (
                                                <Tr key={index}>
                                                    <Td fontSize={'12px'}>{item.brand}</Td>
                                                    <Td fontSize={'12px'}>{item.name}</Td>
                                                    <Td fontSize={'12px'}>{item.barcode}</Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </Collapse>
                            )}
                        </Td>
                    </Tr>
                )}
            </>
        ));
    };

    const renderTableBody = () => {
        return (
            <>
                {isLoading ? (
                    tableBodySkeleton()
                ) : isEmpty(data) && isShowEmptyState ? (
                    <Tr>
                        <Td colSpan={tableHeadLists.length} p={'12px'}>
                            <EmptyState />
                        </Td>
                    </Tr>
                ) : (
                    tableBodyMapping()
                )}
            </>
        );
    };

    return (
        <Table variant="simple" minW={'800px'}>
            <Thead>
                <Tr>{tableHeadMapping()}</Tr>
            </Thead>

            <Tbody>{renderTableBody()}</Tbody>

            {isPagination && (
                <Tfoot>
                    <Tr>
                        <Td colSpan={tableHeadLists.length} borderBottom={'none'} px={0} ps={'16px'}>
                            <Pagination
                                limit={tableData?.length ?? 0}
                                totalData={tableData?.length}
                                pageCount={splittedData?.length}
                                page={page + 1}
                                onPageChange={onPageChange}
                                onChangeLimit={onChangeLimit}
                            />
                        </Td>
                    </Tr>
                </Tfoot>
            )}
        </Table>
    );
}
